import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PLAYER_ACTIVITY_KIND_WORDS,
  PLAYER_ACTIVITY_OUTCOME_WORDS,
  PLAYER_ACTIVITY_PAGE_DEFAULT,
  type StaffActivityRow,
} from '@dayon/contracts';
import { Button, Field, Icon, Input, Select, useErrorDialog, useToast } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../../lib/api.ts';
import {
  EMPTY_FILTERS,
  activityQueryString,
  exportDays,
  exportFileName,
  mergePage,
  rangeProblem,
  readRefusalTitle,
  type ActivityFilters,
} from '../activity-log.ts';
import { ActivityLogTable } from './ActivityLogTable.tsx';

interface ActivityPage {
  player: { id: string; ref: string };
  items: StaffActivityRow[];
  nextCursor: string | null;
  limit: number;
  /** How long addresses are kept, in days: the period the redaction job runs with. */
  retentionDays: number;
}

interface ActivityExport {
  ref: string;
  rowCount: number;
  truncated: boolean;
  rowCap: number;
  from: string;
  to: string;
  downloadPath: string;
}

/**
 * The Logs tab: what the player did and from where. It owns the filters, the read, the paging and
 * the export; the view it hands them to has no state of its own.
 *
 * The read fires only on mount, and this tab mounts only under the profile's own players.view
 * refusal (capability-gate). A failed READ is drawn in the panel, never as a pop-up and never as an
 * empty list; a failed EXPORT or DOWNLOAD goes to the error dialog.
 */
export function LogsTab({
  playerId,
  playerRef,
  canExport,
  formatMoment,
}: {
  playerId: string;
  playerRef: string;
  canExport: boolean;
  formatMoment: (iso: string) => string;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [filters, setFilters] = useState<ActivityFilters>(EMPTY_FILTERS);
  const [rows, setRows] = useState<StaffActivityRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<ActivityExport | null>(null);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  /* Which read is the latest, so a slow answer to an older filter cannot overwrite a newer one. */
  const latest = useRef(0);
  const downloading = useRef(false);
  const player = encodeURIComponent(playerId);

  const read = useCallback(
    (f: ActivityFilters, after: string | null) => {
      const mine = ++latest.current;
      setLoading(true);
      api
        .get<ActivityPage>(
          `/api/players/${player}/activity?${activityQueryString(f, after, PLAYER_ACTIVITY_PAGE_DEFAULT)}`,
        )
        .then((page) => {
          if (mine !== latest.current) return;
          setRows((prev) => mergePage(prev, page.items, after));
          setCursor(page.nextCursor);
          setRetentionDays(page.retentionDays);
          setFailed(false);
        })
        .catch((error: unknown) => {
          if (mine !== latest.current) return;
          /* The API refused the question (a range it will not read, or a page it did not issue): say
             so in the pop-up, in its own words, and keep what is on screen. Only a failure to answer
             is "could not be read". */
          if (error instanceof ApiRequestError && error.status === 400) {
            showError({
              title: readRefusalTitle(error.fields),
              message: error.message,
              code: error.code,
            });
            return;
          }
          if (after) {
            /* A later page failed: the rows already loaded stay, with the button to try again. */
            showError({
              title: 'Could not load older entries',
              message:
                error instanceof ApiRequestError
                  ? error.message
                  : 'The platform did not answer. Try again shortly.',
              ...(error instanceof ApiRequestError ? { code: error.code } : {}),
            });
            return;
          }
          setRows([]);
          setCursor(null);
          setFailed(true);
        })
        .finally(() => {
          if (mine === latest.current) setLoading(false);
        });
    },
    [player, showError],
  );

  useEffect(() => {
    read(EMPTY_FILTERS, null);
  }, [read]);

  const change = (patch: Partial<ActivityFilters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setResult(null);
    const problem = rangeProblem(next.from, next.to);
    if (problem) {
      showError({ title: 'Check the days', message: problem });
      return;
    }
    read(next, null);
  };

  const runExport = () => {
    if (exporting) return;
    const { from, to } = exportDays(filters);
    const problem = rangeProblem(from, to);
    if (problem) {
      showError({ title: 'Check the days', message: problem });
      return;
    }
    setExporting(true);
    void (async () => {
      try {
        setResult(
          await api.post<ActivityExport>(`/api/players/${player}/activity/export`, {
            from,
            to,
            ...(filters.kind ? { kind: filters.kind } : {}),
            ...(filters.outcome ? { outcome: filters.outcome } : {}),
          }),
        );
      } catch (error: unknown) {
        showError({
          title: 'The log was not exported',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'Nothing was produced. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setExporting(false);
      }
    })();
  };

  /* A fetch rather than a navigation, so a refusal reaches the error dialog instead of replacing
     the console with raw JSON; copied from useAuditExport().download. */
  const download = () => {
    if (!result || downloading.current) return;
    downloading.current = true;
    void (async () => {
      try {
        const response = await fetch(result.downloadPath, { credentials: 'same-origin' });
        if (!response.ok) {
          const body = (await response.json()) as { error?: { code: string; message: string } };
          throw new ApiRequestError(response.status, {
            code: body.error?.code ?? 'INTERNAL',
            message: body.error?.message ?? 'The file could not be read.',
          });
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = exportFileName(playerRef, result);
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        /* Revoked on the next task: Chromium reads the blob after the click returns. */
        setTimeout(() => URL.revokeObjectURL(url), 0);
        toast(`${result.ref} downloaded.`);
      } catch (error: unknown) {
        showError({
          title: 'The file could not be downloaded',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The export exists. Try the download again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        downloading.current = false;
      }
    })();
  };

  const filterControls = (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      }}
    >
      <Field label="What happened">
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={filters.kind}
            onChange={(e) => change({ kind: e.target.value })}
          >
            <option value="">Everything</option>
            {Object.entries(PLAYER_ACTIVITY_KIND_WORDS).map(([kind, words]) => (
              <option key={kind} value={kind}>
                {words.done}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Outcome">
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={filters.outcome}
            onChange={(e) => change({ outcome: e.target.value })}
          >
            <option value="">Any outcome</option>
            {Object.entries(PLAYER_ACTIVITY_OUTCOME_WORDS).map(([outcome, words]) => (
              <option key={outcome} value={outcome}>
                {words}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="From">
        {({ id, describedBy }) => (
          <Input
            id={id}
            type="date"
            aria-describedby={describedBy}
            value={filters.from}
            onChange={(e) => change({ from: e.target.value })}
          />
        )}
      </Field>
      <Field label="To">
        {({ id, describedBy }) => (
          <Input
            id={id}
            type="date"
            aria-describedby={describedBy}
            value={filters.to}
            onChange={(e) => change({ to: e.target.value })}
          />
        )}
      </Field>
    </div>
  );

  const exportResult = result && (
    <div className="notice notice-quiet">
      <Icon name={result.truncated ? 'warning-circle' : 'check-circle'} />
      <div>
        <strong>{result.ref}</strong> · {formatCount(result.rowCount)} row
        {result.rowCount === 1 ? '' : 's'} for {result.from} to {result.to}.
        {result.truncated && (
          <>
            {' '}
            <strong>
              That is the {formatCount(result.rowCap)}-row cap, so the file is INCOMPLETE.
            </strong>{' '}
            Narrow the days and export again.
          </>
        )}
        <div style={{ marginTop: 'var(--space-2)' }}>
          <Button small onClick={download}>
            <Icon name="download-simple" /> Download
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <ActivityLogTable
      rows={rows}
      loading={loading}
      failed={failed}
      hasMore={cursor !== null}
      canExport={canExport}
      retentionDays={retentionDays}
      formatMoment={formatMoment}
      onMore={() => read(filters, cursor)}
      onExport={runExport}
      filters={filterControls}
      exportResult={exportResult}
      exporting={exporting}
    />
  );
}
